import React, { useState } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, Users, LogOut, Mail, Menu, X, Settings, FolderOpen, Tag, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';

const AdminLayout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  console.log('AdminLayout - User:', user);
  console.log('AdminLayout - isAdmin:', isAdmin());

  if (!user) {
    console.log('No user, redirecting to login');
    navigate('/login');
    return null;
  }

  if (!isAdmin()) {
    console.log('User is not admin, redirecting to home');
    navigate('/');
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const allMenuItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, permission: null },
    { path: '/admin/products', label: 'Products', icon: Package, permission: 'products' },
    { path: '/admin/categories', label: 'Categories', icon: FolderOpen, permission: 'super_admin_only' },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart, permission: 'orders' },
    { path: '/admin/pricing', label: 'Pricing Rules', icon: Tag, permission: 'orders' },
    { path: '/admin/visibility', label: 'Product Access', icon: Lock, permission: 'products' },
    { path: '/admin/users', label: 'Users', icon: Users, permission: 'users' },
    { path: '/admin/customers', label: 'Customers', icon: Users, permission: 'customers' },
    { path: '/admin/webmail', label: 'Webmail', icon: Mail, permission: null },
    { path: '/admin/settings', label: 'Settings', icon: Settings, permission: 'super_admin_only' }
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => {
    if (!item.permission) return true; // Dashboard and Webmail are always visible
    if (item.permission === 'super_admin_only') return user.role === 'super_admin'; // Only super admin
    if (user.role === 'super_admin') return true; // Super admin sees everything
    return user.permissions && user.permissions.includes(item.permission);
  });

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-800 text-white rounded-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-800 text-white flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 sm:p-6 border-b border-slate-700">
          <h2 className="text-xl sm:text-2xl font-bold">Admin Panel</h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-1 truncate">{user?.name}</p>
        </div>
        <nav className="flex-1 p-2 sm:p-4 overflow-y-auto">
          <ul className="space-y-1 sm:space-y-2">
            {menuItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={closeSidebar}
                    className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-colors text-sm sm:text-base ${
                      isActive
                        ? 'bg-purple-600 text-white'
                        : 'text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="p-2 sm:p-4 border-t border-slate-700">
          <Link to="/" onClick={closeSidebar}>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:bg-slate-700 hover:text-white text-sm mb-2"
            >
              ← Back to Store
            </Button>
          </Link>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:bg-slate-700 hover:text-white text-sm"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto lg:ml-0 pt-16 lg:pt-0">
        <Outlet />
      </div>
    </div>
  );
};

export default AdminLayout;